import { createContext, useContext, useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { hasTabSession, clearTabToken } from '../lib/tabSession';
import { IdleSessionGuard } from '../components/IdleSessionGuard';

const AuthContext = createContext(null);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const revision = useRef(0);
  const publishIdentity = useCallback(value => {
    // A per-tab demo window is intentionally isolated: it must NOT broadcast
    // its impersonated identity to the admin's real windows.
    if (hasTabSession()) return;
    try {
      const identity = value?.user_id || '';
      if (localStorage.getItem('necs-auth-identity') !== identity) localStorage.setItem('necs-auth-identity', identity);
    } catch { /* Cookie authentication still works when browser storage is unavailable. */ }
  }, []);
  const setUser = useCallback(value => {
    revision.current += 1;
    setUserState(value); publishIdentity(value); setLoading(false);
  }, [publishIdentity]);

  const checkAuth = useCallback(async () => {
    const requestRevision = ++revision.current;
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const userData = await response.json();
        if (requestRevision !== revision.current) return;
        setUserState(previous => JSON.stringify(previous) === JSON.stringify(userData) ? previous : userData);
        publishIdentity(userData);
      } else {
        if (requestRevision === revision.current && [401,403].includes(response.status)) { setUserState(null); publishIdentity(null); }
      }
    } catch (error) {
      // A transient network failure is not proof that the server session expired.
    } finally {
      if (requestRevision === revision.current) setLoading(false);
    }
  }, [publishIdentity]);

  useEffect(() => {
    checkAuth();
    const onFocus = () => checkAuth();
    const onVisible = () => { if (document.visibilityState === 'visible') checkAuth(); };
    const onStorage = event => {
      // Ignore cross-tab identity broadcasts in an isolated demo window so it
      // stays pinned to its own tab-scoped session.
      if (hasTabSession()) return;
      if (event.key === 'necs-auth-identity') checkAuth();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('storage', onStorage);
    return () => { window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('storage', onStorage); };
  }, [checkAuth]);

  const logout = async () => {
    // A per-tab demo window is a throwaway impersonation held only in this
    // tab's sessionStorage. Signing out must NOT touch the admin's shared
    // cookie/session — just drop this tab's token and close the window.
    if (hasTabSession()) {
      try {
        await fetch(`${BACKEND_URL}/api/auth/logout`, { method: 'POST', credentials: 'omit' });
      } catch (error) { /* best-effort */ }
      clearTabToken();
      setUser(null);
      window.close();
      // If the browser refused to close the tab (not script-opened), fall back.
      window.location.href = '/login';
      return;
    }
    // If this session is an active demo/employee impersonation, "Sign out"
    // should return the admin to their original session — NOT go to /login.
    // /api/auth/me returns `impersonated_by` when impersonation is active.
    if (user && user.impersonated_by) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/end-impersonation`, {
          method: 'POST',
          credentials: 'include',
        });
        if (res.ok) {
          // Hard reload so AuthContext refetches /me with the admin cookie
          // and the user lands back on the company manager dashboard.
          window.location.href = '/company-manager-dashboard';
          return;
        }
      } catch (e) {
        console.error('end-impersonation failed, falling back to normal logout:', e);
      }
    }
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      window.location.href = '/login';
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      <Fragment key={user?.user_id || 'signed-out'}>{children}</Fragment>
      <IdleSessionGuard user={user} onExpired={() => { setUser(null); window.location.assign('/login?reason=inactivity'); }} />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};